import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { parseCatalogSearch, toCatalogSearch } from '@/lib/catalog';
import type { CatalogFilterState } from '@/lib/catalog';

export type UpdateMode = 'push' | 'replace';

/**
 * The shop's filters live in the URL (`/shop?color=black&finish=polished&sort=price_asc`): shareable,
 * back-button friendly, and the single source of truth. `update` writes a new state — `push` adds a
 * history entry (a deliberate choice: a filter, a sort), `replace` does not (typing, dragging a
 * slider). The page never scrolls to the top on its own.
 */
export function useShopFilters(): {
  filters: CatalogFilterState;
  update: (next: CatalogFilterState, mode?: UpdateMode) => void;
  /** True when the URL carries anything beyond the plain `/shop`. */
  isFiltered: boolean;
} {
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => parseCatalogSearch(params), [params]);
  const update = useCallback(
    (next: CatalogFilterState, mode: UpdateMode = 'push') => {
      setParams(new URLSearchParams(toCatalogSearch(next)), {
        replace: mode === 'replace',
        preventScrollReset: true,
      });
    },
    [setParams],
  );
  return { filters, update, isFiltered: toCatalogSearch(filters) !== '' };
}
